import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const TABS = [
  { id: "login", label: "Sign in" },
  { id: "signup", label: "Create account" },
];

const GoogleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
);

export const AuthPanel = ({ next }) => {
  const { setSession } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (tab === "forgot") {
        await api.post("/auth/forgot-password", { email: form.email });
        toast.success("If the account exists, a reset link is on its way.");
        setTab("login");
      } else {
        const body = tab === "login" ? { email: form.email, password: form.password } : form;
        const { data } = await api.post(tab === "login" ? "/auth/login" : "/auth/register", body);
        const u = setSession(data);
        nav(u.role === "admin" ? "/admin" : next || "/dashboard", { replace: true });
      }
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const google = () => {
    if (next) localStorage.setItem("aurora_next", next);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="holo-panel corners matrix-edge fade-up p-6 sm:p-10" style={{ animationDelay: "200ms" }} data-testid="auth-panel">
      <div className="eyebrow">Access // SpeechFlow</div>
      <h2 className="mt-3 font-display text-2xl font-extrabold tracking-tight">
        {tab === "forgot" ? "Reset password" : tab === "login" ? "Welcome back" : "Join the room"}
      </h2>
      {tab !== "forgot" && (
        <div className="mt-8 grid grid-cols-2 border border-border">
          {TABS.map((t) => (
            <button key={t.id} type="button" onClick={() => setTab(t.id)} data-testid={`auth-tab-${t.id}`}
              className={`h-10 text-sm font-medium transition-colors duration-200 ${tab === t.id ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}>
              {t.label}
            </button>
          ))}
        </div>
      )}
      <form onSubmit={submit} className="mt-6 space-y-4">
        {tab === "signup" && (
          <label className="block">
            <span className="mono-label">Name</span>
            <input className="field mt-2" value={form.name} onChange={set("name")} required data-testid="auth-input-name" />
          </label>
        )}
        <label className="block">
          <span className="mono-label">Email</span>
          <input className="field mt-2" value={form.email} onChange={set("email")} required autoComplete="username"
            type={tab === "login" ? "text" : "email"} data-testid="auth-input-email" />
        </label>
        {tab !== "forgot" && (
          <label className="block">
            <span className="mono-label">Password</span>
            <input className="field mt-2" type="password" value={form.password} onChange={set("password")} required minLength={tab === "signup" ? 6 : 1}
              autoComplete={tab === "login" ? "current-password" : "new-password"} data-testid="auth-input-password" />
          </label>
        )}
        <button className="btn-primary w-full" disabled={busy} data-testid="auth-submit-button">
          {busy ? "Processing…" : tab === "forgot" ? "Send reset link" : tab === "login" ? "Sign in" : "Create account"}
          <ArrowRight size={16} />
        </button>
      </form>
      {tab !== "forgot" ? (
        <>
          <div className="my-6 flex items-center gap-4"><span className="h-px flex-1 bg-border" /><span className="mono-label">or</span><span className="h-px flex-1 bg-border" /></div>
          <button onClick={google} className="btn-ghost w-full" data-testid="auth-google-button"><GoogleIcon /> Continue with Google</button>
          <button onClick={() => setTab("forgot")} className="mt-6 font-mono text-xs tracking-wider text-muted-foreground hover:text-[color:var(--au-green)]" data-testid="auth-forgot-password-button">
            FORGOT PASSWORD?
          </button>
        </>
      ) : (
        <button onClick={() => setTab("login")} className="mt-6 font-mono text-xs tracking-wider text-muted-foreground hover:text-[color:var(--au-green)]" data-testid="auth-back-to-login">
          ← BACK TO SIGN IN
        </button>
      )}
    </div>
  );
};
